import type { EmailActivity } from '@common/types/activities';
import SouthIcon from '@mui/icons-material/South';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import QuoteItem from '../QuoteItem';
import ShowLessButton from '../ShowLessButton';
import { cleanHTML } from '../util';

interface Props {
    emailBody: EmailActivity['body'];
    isBodyExpanded: boolean;
    setIsBodyExpanded: (val: boolean) => void;
}

export default function QuoteEmailToLawyerForApprovalBody({
    emailBody,
    isBodyExpanded,
    setIsBodyExpanded,
}: Props) {
    const theme = useTheme();
    const isAlberta = emailBody.LegalFeesWithGst;

    let priceSectionContent = '';
    let clientNameInApproval = '';
    if (emailBody?.SalePrice) {
        priceSectionContent = `Sale price: ${emailBody?.SalePrice}`;
        clientNameInApproval = emailBody?.ClientFirstName || '';
    } else if (emailBody?.PurchasePrice) {
        priceSectionContent = `Purchase price: ${emailBody?.PurchasePrice}`;
        clientNameInApproval = emailBody?.ClientFirstName || '';
    } else if (emailBody?.TotalLoanAmount) {
        priceSectionContent = `Total loan amount: ${emailBody?.TotalLoanAmount}`;
        clientNameInApproval = emailBody?.ClientName || '';
    }

    return (
        <Box>
            <Typography
                variant="h4"
                sx={{
                    margin: '16px 0',
                }}
            >
                Approval required
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', marginTop: '16px' }}>
                Client name: {emailBody?.ClientName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', marginTop: '16px' }}>
                {priceSectionContent}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', marginTop: '16px' }}>
                This email includes an auto-generated quote for {emailBody?.ClientName}, who
                submitted a quote request. Once you 'Approve' below, Lexo will send the below quote
                to the client on your behalf.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', marginTop: '16px' }}>
                You can modify the email's text, approvals or reminder cycle, by clicking on “Modify
                quote” button.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', margin: '16px 0' }}>
                Lexo will also follow-up with the client per the reminder cycles set in the quote
                structure. You can also auto-approve these emails in by changing the Approval
                settings in quote structure.
            </Typography>
            <hr />
            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.neutral.gray,
                    marginTop: '16px',
                    textAlign: 'center',
                }}
            >
                Your quote template below. <br />
                <SouthIcon sx={{ color: theme.palette.neutral.darkBlue, margin: '8px 0' }} />
            </Typography>
            <hr />

            <Box
                sx={{
                    marginTop: '16px',
                    wordWrap: 'break-word',
                    overflowWrap: 'anywhere',
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.primary',
                        marginBottom: '16px',
                    }}
                >
                    Hello {clientNameInApproval},
                </Typography>
                <Typography variant="body2" color="text.primary">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: cleanHTML(emailBody?.EmailBodyIntroduction || ''),
                        }}
                    ></div>
                </Typography>
                <Box
                    sx={{
                        marginTop: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                    }}
                >
                    <QuoteItem
                        itemLabel={`Legal fee with ${isAlberta ? 'GST' : 'HST'}:`}
                        value={
                            isAlberta ? emailBody?.LegalFeesWithGst : emailBody?.LegalFeesWithHst
                        }
                    />
                    <QuoteItem
                        itemLabel={`Estimated disbursements with ${isAlberta ? 'GST' : 'HST'}:`}
                        value={
                            isAlberta
                                ? emailBody?.DisbrusmentWithGst
                                : emailBody?.DisbrusmentWithHst
                        }
                    />
                    {emailBody?.TitleInsuranceFees && (
                        <QuoteItem
                            itemLabel="Estimated title insurance fee:"
                            value={emailBody?.TitleInsuranceFees}
                        />
                    )}
                    {emailBody?.RegistrationFees && (
                        <QuoteItem
                            itemLabel="Estimated registration fees:"
                            value={emailBody?.RegistrationFees}
                        />
                    )}
                    {emailBody?.LandTransferTax && (
                        <QuoteItem
                            itemLabel="Land transfer tax:"
                            value={emailBody?.LandTransferTax}
                        />
                    )}
                    {emailBody?.FirstTimeHomeBuyerRebate && (
                        <QuoteItem
                            itemLabel="First time home buyer rebate:"
                            value={emailBody?.FirstTimeHomeBuyerRebate}
                        />
                    )}
                    {emailBody?.NetLandTransferTax && (
                        <QuoteItem
                            itemLabel="Net Land Transfer Tax:"
                            value={emailBody?.NetLandTransferTax}
                        />
                    )}
                </Box>
                <Divider sx={{ margin: '16px 0px' }} />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Quote total:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {emailBody?.QuoteTotal}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.primary', marginTop: '16px' }}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: cleanHTML(emailBody?.EmailBodyClosing || ''),
                        }}
                    ></div>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', marginTop: '16px' }}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: cleanHTML(emailBody?.EmailSignature || ''),
                        }}
                    ></div>
                </Typography>
                <ShowLessButton
                    isBodyExpanded={isBodyExpanded}
                    bodyExpandedHandler={setIsBodyExpanded}
                />
            </Box>
        </Box>
    );
}
